import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { VacationHelp, VACATION_APPROVAL_STEPS, APPROVAL_RULES } from '@/features/Vacations/components/VacationHelp';
import { ApprovalCarousel } from './components/ApprovalCarousel';
import { ApprovalDecidedList } from './components/ApprovalDecidedList';
import { ApprovalDecisionModal } from './components/ApprovalDecisionModal';
import { ApprovalQueueFilters } from './components/ApprovalQueueFilters';
import { ApprovalQueueToolbar } from './components/ApprovalQueueToolbar';
import { ApprovalTypeFilter } from './components/ApprovalTypeFilter';
import { TourDemoApprovalCards } from './components/TourDemoApprovalCards';
import { buildDecidedDemoItems } from './components/approvalTourDemo.definitions';
import { useApprovalsQueue } from './hooks/useApprovalsQueue';
import { fromVacation } from './utils/approvalAdapters';
import {
    EMPTY_ALL_DECIDED_MESSAGE,
    EMPTY_SEARCH_MESSAGE,
    ERROR_TITLES,
    PAGE_SUBTITLE,
    PAGE_TITLE,
} from './approvals.definitions';
import type { ApprovalKind } from './approvals.types';

const ERROR_KINDS: ApprovalKind[] = ['vacation', 'day_off'];

export function ApprovalsPage() {
    const { user } = useAuth();
    const {
        pendingItems,
        decidedItems,
        isLoading,
        errors,
        pendingCount,
        pendingByKind,
        decidedCount,
        oldestPendingAt,
        search,
        setSearch,
        sortOrder,
        setSortOrder,
        typeFilter,
        setTypeFilter,
        decisionItem,
        decisionKind,
        openDecision,
        closeDecision,
        confirmDecision,
        isDeciding,
    } = useApprovalsQueue(user?.email ?? undefined);

    const [isTourDemo, setTourDemo] = useState(false);
    const demoDecidedItems = useMemo(
        () => buildDecidedDemoItems(new Date()).map(fromVacation),
        []
    );

    const emptyMessage = search.trim()
        ? EMPTY_SEARCH_MESSAGE
        : decidedCount > 0
          ? EMPTY_ALL_DECIDED_MESSAGE
          : undefined;

    return (
        <div className="space-y-6 pb-10">
            <SectionHeader
                title={PAGE_TITLE}
                subtitle={PAGE_SUBTITLE}
                actions={
                    <VacationHelp
                        tourId="approval"
                        steps={VACATION_APPROVAL_STEPS}
                        rules={APPROVAL_RULES}
                        autoStartTour={false}
                        onTourStart={() => setTourDemo(true)}
                        onTourEnd={() => setTourDemo(false)}
                    />
                }
            />

            {ERROR_KINDS.map((kind) =>
                errors[kind] ? (
                    <div key={kind} className="rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
                        <p className="text-sm font-medium text-red-800 font-montserrat">{ERROR_TITLES[kind]}</p>
                        <p className="mt-1 text-sm text-red-700 font-montserrat">{errors[kind]?.message}</p>
                    </div>
                ) : null
            )}

            <div data-tour="approval-toolbar">
                <ApprovalQueueToolbar pendingCount={pendingCount} oldestPendingAt={oldestPendingAt} />
            </div>

            <div data-tour="approval-filters" className="space-y-3">
                <ApprovalTypeFilter value={typeFilter} counts={pendingByKind} onChange={setTypeFilter} />
                <ApprovalQueueFilters
                    search={search}
                    onSearchChange={setSearch}
                    sortOrder={sortOrder}
                    onSortChange={setSortOrder}
                    visibleCount={pendingItems.length + decidedItems.length}
                />
            </div>

            <div data-tour="approval-carousel">
                {isTourDemo ? (
                    <TourDemoApprovalCards />
                ) : (
                    <ApprovalCarousel
                        items={pendingItems}
                        onDecide={openDecision}
                        isLoading={isLoading}
                        emptyMessage={emptyMessage}
                    />
                )}
            </div>

            <div data-tour="approval-decided-list">
                <ApprovalDecidedList
                    items={isTourDemo ? demoDecidedItems : decidedItems}
                    isLoading={!isTourDemo && isLoading}
                />
            </div>

            <ApprovalDecisionModal
                item={decisionItem}
                kind={decisionKind}
                onClose={closeDecision}
                onConfirm={confirmDecision}
                isSubmitting={isDeciding}
            />
        </div>
    );
}

export default ApprovalsPage;
